import React, { FC, useEffect } from "react";
import Layout from "../../common/layouts/Layout";
import Navbar from "../../common/Navbar/Navbar";
import {
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Grid,
  Box,
  IconButton,
} from "@mui/material";
import IMAGES from "../../../constants/images";
import { CameraAltOutlined } from "@mui/icons-material";
import styles from "./Profile.module.scss";
import ProfileInfoBox from "./ProfileInfoBox/ProfileInfoBox";
import ContactInfo from "./ContactInfo/ContactInfo";
import PasswordSection from "./PasswordSection/PasswordSection";
import { useNavigate } from "react-router-dom";
import { RoutingList } from "../../../constants";
import {
  useGetUserByIdLazyQuery,
  useUpdateUserMutation,
} from "../../../generated/graphql";
import useAuthStore from "../../../states/zustand/authStore";
import { getDate, getTimeStamp } from "../../../utils/helper";
import fileUploadService from "../../../services/fileUpload";
import { logger } from "../../../utils/logger";
import useAppStore from "../../../states/zustand/appStore";

const Profile: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const {
    profileData: { _id },
    setProfileData,
  } = useAuthStore();
  const [getUserData, { data, loading }] = useGetUserByIdLazyQuery();

  const { setLoader } = useAppStore();

  useEffect(() => {
    setLoader(loading);
  }, [loading]);

  const [updateUserMutation] = useUpdateUserMutation();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!e.target.files) return;

    try {
      const imageUrl = await fileUploadService.upload(
        e.target.files,
        `${getTimeStamp()}-profile`
      );
      await updateUserMutation({
        variables: {
          id: _id,
          avatar: imageUrl.data,
        },
      });
      await getUserData({
        variables: {
          id: _id,
        },
      });
      setProfileData({ avatar: imageUrl.data });
    } catch (err) {
      logger.error({ err });
    }
  };

  useEffect(() => {
    const getUser = async () => {
      await getUserData({
        variables: {
          id: _id,
        },
      });
    };
    getUser();
  }, [_id, getUserData]);

  return (
    <Layout>
      <>
        <Navbar pageTitle={`Profile`} />
        <Container maxWidth={false}>
          <div className={`${styles.profilePage}`}>
            <Card className={`mg-b-20`}>
              <CardContent className={`pd-x-0 pd-y-0`}>
                <div className={`pd-x-30 pd-y-24`}>
                  <div
                    className={`d-flex align-items-start justify-content-between mg-b-30`}
                  >
                    <div>
                      <Typography variant={"h1"} component={"h1"}>
                        Profile Info
                      </Typography>
                      <Typography
                        variant={"body1"}
                        component={"p"}
                        color={"secondary"}
                      >
                        A photo help presonalise your account
                      </Typography>
                    </div>
                    <Button
                      variant={"outlined"}
                      color={"error"}
                      className="mg-r-10 btn-sm"
                      type="button"
                      onClick={() =>
                        navigate(RoutingList.profile.edit.path, {
                          state: {
                            data,
                          },
                        })
                      }
                    >
                      <span>Edit</span>
                    </Button>
                  </div>
                  <Grid className="align-items-center" container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <Typography className={`font-weight-medium`}>
                        Photo
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Typography>
                        A photo help personalize your account
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        className={`avatar avatar-circle avatar-90 ${styles.profileBox}`}
                      >
                        <img
                          src={data?.getUserById.avatar || IMAGES.Profile}
                          alt="..."
                        />
                        <IconButton
                          className={styles.fileUpload}
                          color="primary"
                          aria-label="upload picture"
                          component="label"
                        >
                          <input
                            onChange={handleFileUpload}
                            hidden
                            accept="image/*"
                            type="file"
                          />
                          <Box className={styles.cameraIconWrap}>
                            <CameraAltOutlined />
                          </Box>
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </div>
                <ProfileInfoBox title={`Name`} value={data?.getUserById.name} />
                {/* <ProfileInfoBox title={`Birthday`} value={`Aug 08, 1994`} /> */}
                <ProfileInfoBox title={`Role`} value={data?.getUserById.role} />
              </CardContent>
            </Card>
            <ContactInfo title={`Email`} value={data?.getUserById.email} />
            <PasswordSection dateValue={getDate(data?.getUserById.updatedAt)} />
          </div>
        </Container>
      </>
    </Layout>
  );
};

export default Profile;
